import { del, get, post } from '@/common/helpers/api_helper'
import { notifications } from '@/common/helpers/notifications'
import {
  Acomodacion,
  Assigned,
  IBookingDetail as IBookingDetailDeckplan,
  ICabinasInfo,
  EstrucutraArbol,
  ICabinaBloqueada,
  JsonData,
  PasajerosApi,
  PassengerToList,
} from '@/common/interfaces/deckplan'

export const UseGet = (url: string): Promise<any> => {
  return get(url).then((odata) => {
    return odata.value
  })
}

export const UseGetPassengers = (url: string): Promise<any> => {
  return get(url).then((odata) => {
    return odata
  })
}

export const lockCabinApi = (data: any): Promise<any> => {
  return post('BloqueosNumeroTipoCabina', data)
    .then((_resp) => {
      notifications({
        title: `Guardado`,
        message: 'Cabina bloqueada correctamente',
      })

      return _resp
    })
    .catch((error) => {
      notifications({ ex: error })
    })
}

export const unassignOnly = async (id: number): Promise<any> => {
  await del(`AsignacionesPasajeroCabinas(${id})`)
    .catch((error) => {
      notifications({ ex: error })
    })
}

export const unlockCabinApi = async (id: number): Promise<any> => {
  await del(`/BloqueosNumeroTipoCabina(${id})`)
    .then(() => {
      notifications({
        title: `Desbloqueo`,
        message: 'La cabina se desbloqueó correctamente',
      })
    })
    .catch((error) => {
      notifications({ ex: error })
    })
}

interface CorreoDeckplanSend {
  producto: string
  fechaOperacion: string
  htmlTableCabinasDisponibles: string
}

export const correoCabinasRestantes = async (
  url: string,
  dataCorreo: CorreoDeckplanSend,
): Promise<void> => {
  try {
    await post(url, dataCorreo)
    notifications({
      title: 'Éxito',
      message: 'Notificación yield enviada (correo y campana al equipo de revenue)',
      toastType: 'success',
    })
  } catch (error) {
    notifications({ ex: error })
  }
}

export const changeClassCabin = (
  embarcacionStateOld: Array<ICabinasInfo>,
  cabinaOld: number,
  cabFigClassOld: string,
  franjaFigClassOld: string,
): void => {
  for (const i of embarcacionStateOld) {
    if (i.Numero === cabinaOld) {
      i.cantidadPasajeros = 0
      i.cabFigClass = cabFigClassOld
      i.franjaFigClass = franjaFigClassOld
    }
  }
}

/**
 * La extension que se graba en `ExtensionCrucero` al asignar la cabina, a partir de la de los
 * pasajeros que se sueltan. Los tres valores los calcula el backend
 * (`PasajeroEmbarcacionSupervisor`) comparando las fechas de las salidas de la misma embarcacion:
 *
 * - `T`: el pasajero continua en una salida posterior.
 * - `S`: el pasajero viene de una salida anterior.
 * - `N`: no es parte de una salida extendida.
 *
 * `T` gana cuando el grupo mezcla ambas, porque es la que arrastra trabajo: al asignar con `T` se
 * crea ademas la asignacion en la salida siguiente.
 *
 * Antes solo se miraba `T` y todo lo demas caia en `N`, asi que una cabina asignada desde la
 * SEGUNDA salida de una extension quedaba grabada como si no fuera extension. De ahi salian dos
 * de los sintomas del #41137: al desasignarla no aparecia el aviso de que los pasajeros vienen de
 * una salida anterior —el `case 'S'` era inalcanzable y `DesasignarS` codigo muerto— y al recargar
 * la cabina perdia el amarillo de extendida, porque `filterCabins` lo decide por este mismo campo.
 */
export const getExtension = (pasajeros: Array<PassengerToList>): 'T' | 'S' | 'N' => {
  const tempExtension = pasajeros.map((i) => i.extension)

  if (tempExtension.includes('T')) {
    return 'T'
  }

  if (tempExtension.includes('S')) {
    return 'S'
  }

  return 'N'
}

interface IBookingDetailResult {
  bookingCode: string
  salesProfit: string
  bookingDetail: any[]
}

const estructuraArbol = (dataAgrupada: object): IBookingDetailResult[] => {
  const dataArbol = []

  for (const [key] of Object.entries(dataAgrupada)) {
    const newBooking = {
      bookingCode: key,
      salesProfit: '',
      bookingDetail: [],
    }

    dataArbol.push(newBooking)
  }

  return dataArbol
}

const generarAcomodaciones = (i: EstrucutraArbol, j: PasajerosApi): IBookingDetailDeckplan[] => {
  const nuevaAcomodacion = {
    bookingDetailPosteriorId: j.bookingDetailPosteriorId,
    acomodacion: j.acomodacion,
    acomodacionDescripcion: j.acomodacionDescripcion,
    bookingCode: j.bookingCode,
    bookingDetailId: j.bookingDetailId,
    grupo: j.grupo,
    pasajeros: [],
  }

  if (i.bookingCode === j.bookingCode) {
    if (i.bookingDetail.length > 0) {
      let existe = false

      for (const k of i.bookingDetail) {
        if (JSON.stringify(k) === JSON.stringify(nuevaAcomodacion)) {
          existe = true
        }
      }
      if (!existe) {
        i.bookingDetail.push(nuevaAcomodacion)
      }
    } else {
      i.bookingDetail.push(nuevaAcomodacion)
    }
  }

  return i.bookingDetail
}

export const createTreePassengers = async (url: string): Promise<EstrucutraArbol[]> => {
  const r = await UseGetPassengers(url)
  const result = r.reduce(function (q: any, a: any) {
    q[a.bookingCode] = q[a.bookingCode] || []
    q[a.bookingCode].push(a)

    return q
  }, Object.create(null))

  const dataFinal: EstrucutraArbol[] = estructuraArbol(result)

  for (const i of dataFinal) {
    for (const j of r) {
      i.bookingDetail = generarAcomodaciones(i, j)
      if (i.bookingCode === j.bookingCode) {
        i.salesProfit = j.salesProfit
      }
    }
  }

  for (const i of dataFinal) {
    for (const j of i.bookingDetail) {
      for (const k of r) {
        if (
          k.bookingCode === i.bookingCode &&
          k.bookingDetailId === j.bookingDetailId &&
          k.acomodacion === j.acomodacion &&
          k.grupo === j.grupo
        ) {
          const pasajero = {
            pasajeroId: k.pasajeroId,
            salidaEmbarcacionPosteriorId: k.salidaEmbarcacionPosteriorId,
            tipoPasajero: k.tipoPasajero,
            nombre: k.nombre,
            apellido: k.apellido,
            nacionalidad: k.nacionalidad,
            titulo: k.titulo,
            edad: k.edad,
            grupo: k.grupo,
            bookingDetailId: j.bookingDetailId,
            bookingCode: k.bookingCode,
            salesforceCode: k.salesforceCode,
            extension: k.extension,
            bookingDetailPosteriorId: j.bookingDetailPosteriorId,
          }

          j.pasajeros.push(pasajero)
        }
      }
    }
  }

  return dataFinal
}

export const groupBy = (list: Array<object>, keyGetter: any): Map<any, any> => {
  const map = new Map()

  list.forEach((item) => {
    const key = keyGetter(item)
    const collection = map.get(key)

    if (!collection) {
      map.set(key, [item])
    } else {
      collection.push(item)
    }
  })

  return map
}

// Estilos inline (no clases/<style>): los clientes de correo (Outlook en particular) ignoran
// hojas de estilo externas y clases, solo respetan atributos style="" en cada elemento.
const EMAIL_TABLE_HEADER_STYLE =
  'background:#0f2d52;color:#ffffff;text-align:left;padding:10px 14px;' +
  'font-family:Arial,Helvetica,sans-serif;font-size:12px;letter-spacing:0.04em;' +
  'text-transform:uppercase;border:1px solid #0f2d52;'
const EMAIL_TABLE_CELL_STYLE =
  'padding:10px 14px;border:1px solid #e2e5ea;font-family:Arial,Helvetica,sans-serif;' +
  'font-size:13px;color:#1f2937;vertical-align:top;'
const EMAIL_TABLE_QTY_STYLE = `${EMAIL_TABLE_CELL_STYLE}text-align:center;font-weight:bold;width:90px;`

export const tableEmailHtml = (dataGrouped: any): string => {
  let tableEmail =
    `<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;">` +
    `<tr>` +
    `<th style="${EMAIL_TABLE_HEADER_STYLE}">Deck</th>` +
    `<th style="${EMAIL_TABLE_HEADER_STYLE}">Cabinas libres</th>` +
    `<th style="${EMAIL_TABLE_HEADER_STYLE}">Cantidad</th>` +
    `</tr>`

  dataGrouped.forEach((cabin: any, index: number) => {
    const rowStyle = index % 2 === 1 ? 'background:#f4f6f8;' : ''
    const cabinasLibres = cabin.cabins
      .filter((cab: any) => cab.cantidadPasajeros === 0)
      .map((cab: any) => `${cab.TipoCabina.Deck.Sigla}-${cab.idCab}`)
      .join(', ')

    tableEmail +=
      `<tr style="${rowStyle}">` +
      `<td style="${EMAIL_TABLE_CELL_STYLE}">${cabin.deck}</td>` +
      `<td style="${EMAIL_TABLE_CELL_STYLE}">${cabinasLibres}</td>` +
      `<td style="${EMAIL_TABLE_QTY_STYLE}">${cabin.cabins.length}</td>` +
      `</tr>`
  })

  tableEmail += '</table>'

  return tableEmail
}

export const getAssignedPassengers = async (salidaId: number): Promise<Assigned[]> => {
  const cabinasAsignadas = await UseGet(
    `/AsignacionesPasajeroCabinas?$expand=Pasajero,Cabina&$filter=Eliminado eq false and SalidaEmbarcacionId eq ${salidaId}`,
  )
  const pasajerosAsignados: Assigned[] = []

  for (const i of cabinasAsignadas) {
    const newCabin: Assigned = {
      numeroTipoCabinaId: i.NumeroTipoCabinaId,
      cabina: String(i.NumeroCabina),
      cabinaId: i.CabinaId,
      salidaEmbarcacionId: i.SalidaEmbarcacionId,
      isUpgrade: i.EsUpgradeDowngrade,
      extensionCrucero: i.ExtensionCrucero,
      cabinaOrigenId: i.CabinaOrigenId,
      pasajeros: [],
      cabinaAsignadaNombre: i.Cabina.Nombre,
    }

    pasajerosAsignados.push(newCabin)
  }

  // Un grupo por número de cabina, que es lo que supone todo el deckplan (asignar, el modal y
  // desasignar buscan la cabina con find). Se deduplicaba comparando el grupo entero, así que una
  // cabina con dos acomodaciones distintas salía dos veces y, como los pasajeros se cuelgan solo por
  // número de cabina, cada grupo llevaba a todos: «Desasignar todo» los borraba dos veces y la
  // métrica los contaba dos veces (Bug 44663). Los atributos del grupo son los de la primera fila.
  const sinRepetidosPasajerosAsignados = pasajerosAsignados.filter(
    (valorActual, indiceActual, arreglo) =>
      arreglo.findIndex((valorDelArreglo) => valorDelArreglo.cabina === valorActual.cabina) === indiceActual,
  )

  for (const i of sinRepetidosPasajerosAsignados) {
    for (const j of cabinasAsignadas) {
      if (i.cabina === String(j.NumeroCabina)) {
        i.pasajeros.push({
          asignacionPasajeroCabinaId: j.AsignacionPasajeroCabinaId,
          pasajeroId: j.Pasajero.PasajeroId,
          edad: j.EdadPasajero,
          grupo: 0,
          salesforceCode: '',
          tipoPasajero: '',
          nombre: j.Pasajero.Nombre,
          apellido: j.Pasajero.Apellido,
          nacionalidad: j.Pasajero.Nacionalidad,
          titulo: j.Pasajero.Titulo,
          bookingDetailId: j.BookingDetailId,
          bookingCode: j.BookingCode,
          extension: j.ExtensionCrucero,
          salidaEmbarcacionPosteriorId: 0,
        })
      }
    }
  }

  return sinRepetidosPasajerosAsignados
}

export const getCabinasBloqueadas = (
  cabinas: ICabinasInfo[],
  cabinasBloqueadas: ICabinaBloqueada[],
  claseCabina: string,
  claseFranja: string,
): ICabinasInfo[] => {
  if (cabinasBloqueadas.length > 0) {
    for (const i in cabinas) {
      for (const j in cabinasBloqueadas) {
        if (cabinas[i].NumeroTipoCabinaId === cabinasBloqueadas[j].NumeroTipoCabinaId) {
          cabinas[Number(i)].cabFigClass = claseCabina
          cabinas[Number(i)].franjaFigClass = claseFranja
          cabinas[Number(i)].BloqueoNumeroTipoCabinaId = cabinasBloqueadas[j].BloqueoNumeroTipoCabinaId
        }
      }
    }
    for (const i in cabinas) {
      if (cabinas[Number(i)].BloqueoNumeroTipoCabinaId === undefined) {
        cabinas[Number(i)].BloqueoNumeroTipoCabinaId = null
      }
    }
  } else {
    for (const i in cabinas) {
      cabinas[Number(i)].BloqueoNumeroTipoCabinaId = null
    }
  }

  return cabinas
}

export const handleDrag = (
  event: any,
  pasajeros: string,
  bookingDetailId: string,
  acomodacionDrag: string,
  bookingCode: string,
): void => {
  event.dataTransfer.setData('pasajeros', pasajeros)
  event.dataTransfer.setData('bookingDetailId', bookingDetailId)
  event.dataTransfer.setData('accommodation', acomodacionDrag)
  event.dataTransfer.setData('bookingCode', bookingCode)
}

export const handleDragOver = (event: any): void => {
  event.preventDefault()
}

interface IAccommodationResult {
  id: number
  nombre: string
}

export const handleAccommodations = (
  index: number,
  acomodacionqt: string,
  cabinas: ICabinasInfo[],
): IAccommodationResult[] => {
  const tiposAcomodaciones = []

  for (const j of cabinas[index].TipoCabina.Cabina) {
    const tipoCamaCabina = j.Nombre.substring(j.Nombre.length - 1, j.Nombre.length)
    const tipoCamaPasajero = acomodacionqt.substring(acomodacionqt.length - 1, acomodacionqt.length)

    if (tipoCamaPasajero === tipoCamaCabina) {
      const acomodacionObj = {
        id: j.CabinaId,
        nombre: j.Nombre,
      }

      tiposAcomodaciones.push(acomodacionObj)
    }
  }

  return tiposAcomodaciones
}

export const includesAcomodation = (arrayAcomodaciones: Array<Acomodacion>, accommodationqt: string): boolean => {
  let incluedes: boolean = false

  for (const i of arrayAcomodaciones) {
    if (i.nombre === accommodationqt) {
      incluedes = true
      break
    }
  }

  return incluedes
}

export const handleGetJerarquia = (cabinas: ICabinasInfo[], deck: string, tipo: string): number | undefined => {
  let jerarquiaCabinaPasajero = 0

  for (const i of cabinas) {
    if (i.TipoCabina.Sigla === tipo && i.TipoCabina.Deck.Sigla === deck) {
      jerarquiaCabinaPasajero = i.TipoCabina.Jerarquia

      return jerarquiaCabinaPasajero
    }
  }
}

export const handleGetAssignCabinId = (
  cabinas: ICabinasInfo[],
  indice: number,
  acomodacionDrop: string,
): number | undefined => {
  let assignCabinId = 0

  for (const i of cabinas[indice].TipoCabina.Cabina) {
    if (i.Nombre === acomodacionDrop) {
      assignCabinId = i.CabinaId

      return assignCabinId
    }
  }
}

const handleUnirDatosCabinas = (dataOriginal: JsonData, newData: ICabinasInfo[]): ICabinasInfo[] => {
  for (const i in newData) {
    for (const j in dataOriginal.data) {
      if (newData[parseInt(i)].Numero === dataOriginal.data[parseInt(j)].idCab) {
        newData[parseInt(i)] = {
          ...newData[parseInt(i)],
          ...dataOriginal.data[parseInt(j)],
        }
      }
    }
  }

  return newData
}

export const coloresCabinasPinta = (dataCabinas: ICabinasInfo, sigla: string): void => {
  switch (sigla) {
    case 'L':
      dataCabinas.cabFigClass = 'pintaCabsBlue'
      break

    case 'P':
      dataCabinas.cabFigClass = 'pintaCabsGreen'
      break

    default:
      break
  }
}

export const coloresCabinasIsabela = (data: ICabinasInfo, s: string): void => {
  switch (s) {
    case 'S':
      data.cabFigClass = 'isabelaCabsPink'
      break

    case 'F':
      data.cabFigClass = 'isabelaCabsPink'
      break

    case 'C':
      data.cabFigClass = 'isabelaCabsPurple'
      break

    case 'O':
      data.cabFigClass = 'isabelaCabsSandyBrown'
      break

    default:
      break
  }
}

export const coloresCabinasSantaCruz = (newDataCabinas: ICabinasInfo, sgl: string): void => {
  switch (sgl) {
    case 'HE':
      newDataCabinas.cabFigClass = 'santaCruzCabsDarkBlue'
      break

    case 'HF':
      newDataCabinas.cabFigClass = 'santaCruzCabsCadetBlue'
      break

    case 'HV':
      newDataCabinas.cabFigClass = 'santaCruzCabsViolet'
      break

    case 'EE':
      newDataCabinas.cabFigClass = 'santaCruzCabsCoral'
      break

    case 'PE':
      newDataCabinas.cabFigClass = 'santaCruzCabsBrown'
      break

    case 'PD':
      newDataCabinas.cabFigClass = 'santaCruzCabsGolden'
      break

    default:
      break
  }
}

export const filterCabins = async (
  url: string,
  embarcacion: string,
  salidaId: number,
  dataJson: JsonData,
): Promise<ICabinasInfo[]> => {
  let newCabins: Array<ICabinasInfo> = []
  const dataApi = await UseGet(url)
  const cabinasAsignadas = await UseGet(
    `/AsignacionesPasajeroCabinas?$select=AsignacionPasajeroCabinaId,PasajeroId,CabinaId,NumeroTipoCabinaId,NumeroCabina,SalidaEmbarcacionId,EsUpgradeDowngrade,CabinaOrigenId,ExtensionCrucero&$filter=Eliminado eq false and SalidaEmbarcacionId eq ${salidaId}`,
  )
  const cabinasBloqueadas = await UseGet(`/BloqueosNumeroTipoCabina?$filter=SalidaEmbarcacionId eq ${salidaId}`)

  newCabins = dataApi

  handleUnirDatosCabinas(dataJson, newCabins)

  for (const i in newCabins) {
    let contPasajeros: number = 0

    for (const j in cabinasAsignadas) {
      if (newCabins[parseInt(i)].Numero === cabinasAsignadas[parseInt(j)].NumeroCabina) {
        contPasajeros += 1
        switch (cabinasAsignadas[parseInt(j)].EsUpgradeDowngrade) {
          case 'U':
            newCabins[parseInt(i)].franjaFigClass = 'cabsUpgrade'
            break

          case 'D':
            newCabins[parseInt(i)].franjaFigClass = 'cabsDowngrade'
            break

          default:
            break
        }
        if (cabinasAsignadas[j].ExtensionCrucero === 'T' || cabinasAsignadas[j].ExtensionCrucero === 'S') {
          newCabins[Number(i)].cabFigClass = 'cabsExtended'
        } else {
          switch (embarcacion) {
            case 'santaCruz':
              coloresCabinasSantaCruz(
                newCabins[Number(i)],
                newCabins[parseInt(i)].TipoCabina.Deck.Sigla + newCabins[parseInt(i)].TipoCabina.Sigla,
              )
              break

            case 'isabela':
              coloresCabinasIsabela(newCabins[Number(i)], newCabins[Number(i)].TipoCabina.Sigla)
              break

            case 'pinta':
              coloresCabinasPinta(newCabins[Number(i)], newCabins[Number(i)].TipoCabina.Sigla)
              break

            default:
              break
          }
        }
        newCabins[parseInt(i)].cantidadPasajeros = contPasajeros
      }
    }
  }

  newCabins = getCabinasBloqueadas(newCabins, cabinasBloqueadas, 'cabsGrey', 'cabsBlock')

  return newCabins
}

// Removed sendNotification function - previously used Revenhub (deprecated)
// Las notificaciones las manda el backend a la campana

/**
 * Las salidas posteriores de los pasajeros arrastrados, sin repetir y sin el 0.
 *
 * Las dos validaciones de abajo preguntaban si ALGUNO tenia salida posterior y despues consultaban
 * con la del PRIMERO. Si el primero no tenia -su id es 0- pero otro si, la consulta salia con 0,
 * no encontraba nada y la validacion pasaba en silencio: se dejaba asignar sobre una cabina
 * bloqueada u ocupada en la salida siguiente. Y si los pasajeros arrastrados iban a salidas
 * posteriores distintas, solo se miraba una.
 */
export const salidasPosterioresDe = (pasajeros: PassengerToList[]): number[] => [
  ...new Set(pasajeros.map((p) => p.salidaEmbarcacionPosteriorId).filter((id) => id !== 0)),
]

const hayRegistroEnSalidasPosteriores = async (
  entidad: string,
  newPasajeros: PassengerToList[],
  cabinas: ICabinasInfo[],
  cabina: number,
  filtroExtra = '',
): Promise<boolean | undefined> => {
  const salidas = salidasPosterioresDe(newPasajeros)

  // Sin ninguna salida posterior no hay nada que validar. Se devuelve `undefined`, igual que
  // antes, porque quien llama distingue ese caso del `false`.
  if (salidas.length === 0) {
    return undefined
  }

  const cabinaDroppedId = cabinas.filter((x) => x.Numero === cabina)[0].NumeroTipoCabinaId
  const enSalidaSiguiente = await get(
    `${entidad}?$filter=NumeroTipoCabinaId eq ${cabinaDroppedId} and SalidaEmbarcacionId in (${salidas.join(',')})${filtroExtra}`,
  )

  return enSalidaSiguiente.value.length > 0
}

/**
 * Filtro OData que deja fuera a los pasajeros que se estan asignando en este momento.
 *
 * Sin esto, la cabina que el propio pasajero ya ocupa en la salida siguiente cuenta como
 * "ocupada" y bloquea su propia asignacion: es el caso del #41137 en que asignar primero en la
 * segunda salida dejaba la primera sin poder asignar. Ocupada por OTRO pasajero si tiene que
 * seguir bloqueando, que es para lo que existe la validacion.
 *
 * Se arma con `ne` encadenados en vez de `not (PasajeroId in (...))` porque la negacion del `in`
 * no la soportan todas las versiones del proveedor OData y esto es equivalente.
 */
const exceptoLosPropios = (newPasajeros: PassengerToList[]): string =>
  newPasajeros
    .map((p) => p.pasajeroId)
    .filter((id) => Boolean(id))
    .map((id) => ` and PasajeroId ne ${id}`)
    .join('')

export const validacionBloqueoSalidaSiguiente = (
  newPasajeros: PassengerToList[],
  cabinas: ICabinasInfo[],
  cabina: number,
): Promise<boolean | undefined> =>
  hayRegistroEnSalidasPosteriores('BloqueosNumeroTipoCabina', newPasajeros, cabinas, cabina)

export const validacionCabinaAsignadaSalidaSiguiente = (
  newPasajeros: PassengerToList[],
  cabinas: ICabinasInfo[],
  cabina: number,
): Promise<boolean | undefined> =>
  hayRegistroEnSalidasPosteriores(
    'AsignacionesPasajeroCabinas',
    newPasajeros,
    cabinas,
    cabina,
    // `Eliminado eq false` para no contar asignaciones dadas de baja, igual que hacen
    // `filterCabins` y la desasignacion en cascada.
    ` and Eliminado eq false${exceptoLosPropios(newPasajeros)}`,
  )

// El backend puede devolver titulo/apellido/nombre como null (o el literal 'null');
// arma el nombre visible solo con las partes válidas
export const nombrePasajero = (p: {
  titulo?: string | null
  apellido?: string | null
  nombre?: string | null
}): string => {
  const limpiar = (v?: string | null): string => (v && v.trim().toLowerCase() !== 'null' ? v.trim() : '')

  return [limpiar(p.titulo), limpiar(p.apellido), limpiar(p.nombre)].filter(Boolean).join(' ')
}
